<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  nextTick,
  watch,
} from 'vue';
import axios from '@/axiosConfig';
import eventBus from './eventBus.js';
import MotdModal from './MotdModal.vue'; // Import the MOTD modal component
import html2canvas from 'html2canvas';
import { io } from 'socket.io-client';
import { cashAmountFieldText } from '@/utils/cashAmountDisplay';
import {
  cashItemDayFromSnapshot,
  filterCashItemDayInput,
  commitCashItemDay,
} from '@/utils/cashItemDay';
import {
  previousCycleRange,
  currentCycleRange,
  cumulativeDepositByDay,
  forecastDepositByDay,
  projectionMonthLength,
  buildCashFlowRows,
  formatProjectionMoney,
  formatProjectionAmount,
  cashFlowPrintDocument,
  filterCashFlowRows,
  cashFlowDescriptionWord,
  cashFlowDescriptionPreview,
  isProjectionFriday,
  isFirstProjectionFriday,
  isFirstCurrentDayRow,
  isPositiveCashFlowAmount,
  isLargeCashFlowExpense,
  currentCycleDepositTotal,
  endingBalanceByProjectionDay,
  projectionFetchEnd,
  projectedBalanceTooltip,
  snapshotEndTotal,
  snapshotSlackTotal,
  canEditCashFlowRow,
  commitCashFlowRowEdit,
  cashFlowAsOfLabel,
} from '@/utils/cashFlowProjection';
import { sortCashItems, nextCashSort } from '@/utils/cashItemSort';
import {
  renumberCashSequences,
  nextCashItemSequence,
  weeklyCashItemCopies,
} from '@/utils/cashItemSequence';
import { cashSnapshotOpenChoice } from '@/utils/cashSnapshotOpen';

const title = ref('Snapshot');

// Load last viewed date from localStorage, or use today's date as fallback
const getInitialDate = () => {
  const savedDate = localStorage.getItem('mcashPositionLastDate');
  if (savedDate) {
    return savedDate;
  }
  return new Date().toISOString().substr(0, 10);
};

const selectedDate = ref(getInitialDate());
const canEdit = ref(false); // Control editing state
const userRole = ref(0); // Default user role
const userId = ref(null);
let socket = null;

// Fetch user role from API
const fetchUserRole = async () => {
  try {
    const response = await axios.get('/api/user-info');
    if (response.data && response.data.role !== undefined) {
      userRole.value = response.data.role;
    }
    if (response.data && response.data.id !== undefined) {
      userId.value = response.data.id;
    }
  } catch (error) {
    console.error('Error fetching user role:', error);
  }
};

const normalizeDateKey = (dateVal) => {
  if (!dateVal) return '';
  const s = String(dateVal);
  // Handle ISO timestamps or YYYY-MM-DD
  return s.length >= 10 ? s.slice(0, 10) : s;
};

const handleCashPositionUpdated = async (data) => {
  if (!data?.selectedDate) return;
  if (normalizeDateKey(data.selectedDate) !== normalizeDateKey(selectedDate.value)) {
    return;
  }
  // Ignore events from our own saves so blur-save does not exit edit mode
  if (
    data.userId != null &&
    userId.value != null &&
    Number(data.userId) === Number(userId.value)
  ) {
    return;
  }
  canEdit.value = false;
  await fetchData(selectedDate.value);
};

const connectSocket = () => {
  try {
    const socketUrl = process.env.VUE_APP_SOCKET_URL;
    if (!socketUrl) {
      console.error('MCashPosition: Socket URL is undefined.');
      return;
    }
    const token = localStorage.getItem('token');
    socket = io(socketUrl, {
      query: {
        token,
        source: 'cashPosition',
      },
    });
    socket.on('cashPositionUpdated', handleCashPositionUpdated);
  } catch (error) {
    console.error('MCashPosition: Error connecting socket:', error);
  }
};

const disconnectSocket = () => {
  if (!socket) return;
  socket.off('cashPositionUpdated', handleCashPositionUpdated);
  socket.disconnect();
  socket = null;
};

const cashItems = ref([]);
const cashSortKey = ref('sequence');
const cashSortDir = ref('asc');
const displayedCashItems = computed(() =>
  sortCashItems(cashItems.value, cashSortKey.value, cashSortDir.value),
);
const toggleCashSort = (key) => {
  const next = nextCashSort(cashSortKey.value, cashSortDir.value, key);
  cashSortKey.value = next.key;
  cashSortDir.value = next.dir;
};
const cashSortIcon = (key) => {
  if (cashSortKey.value !== key) return 'fa-sort';
  return cashSortDir.value === 'asc' ? 'fa-caret-up' : 'fa-caret-down';
};
const cashSortTitle = (key) => {
  const label = key === 'day' ? 'day' : 'sequence';
  if (cashSortKey.value !== key) return `Sort by ${label}`;
  const direction = cashSortDir.value === 'asc' ? 'ascending' : 'descending';
  return `Sort by ${label} ${direction}`;
};
const scrollableContentRef = ref(null);
const showScrollArrow = ref(false);
let scrollResizeObserver = null;

const updateScrollArrowVisibility = () => {
  const el = scrollableContentRef.value;
  if (!el) {
    showScrollArrow.value = false;
    return;
  }
  const hasOverflow = el.scrollHeight > el.clientHeight + 1;
  const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
  showScrollArrow.value = hasOverflow && !atBottom;
};

const scrollCashItemsToBottom = (behavior = 'smooth') => {
  const el = scrollableContentRef.value;
  if (!el) return;
  el.scrollTo({ top: el.scrollHeight, behavior });
};
const selectedRows = ref(new Set());
const rowPercentages = ref(new Map());
const showPercentageModal = ref(false);
const percentageModalItem = ref(null);
const percentageInput = ref('');
const percentageInputRef = ref(null);
const showHighlightPicker = ref(false);
const highlightPickerIndex = ref(null);
const notes = ref('');

const HIGHLIGHT_OPTIONS = [
  { id: 'yellow', label: 'Yellow', print: 'rgba(255, 235, 59, 0.45)' },
  { id: 'green', label: 'Green', print: 'rgba(129, 199, 132, 0.45)' },
  { id: 'orange', label: 'Orange', print: 'rgba(255, 183, 77, 0.45)' },
  { id: 'red', label: 'Red', print: 'rgba(239, 154, 154, 0.45)' },
  { id: 'blue', label: 'Blue', print: 'rgba(100, 181, 246, 0.45)' },
  { id: 'none', label: 'None', print: null },
];

const HIGHLIGHT_PRINT_COLORS = Object.fromEntries(
  HIGHLIGHT_OPTIONS.map(({ id, print }) => [id, print]),
);

const getHighlightPreviewColor = (colorId) =>
  HIGHLIGHT_OPTIONS.find((option) => option.id === colorId)?.print || null;

const normalizeHighlightColor = (color) =>
  HIGHLIGHT_OPTIONS.some((option) => option.id === color) ? color : 'none';

const openHighlightPicker = (index) => {
  if (!canEdit.value) return;
  highlightPickerIndex.value = index;
  showHighlightPicker.value = true;
};

const closeHighlightPicker = () => {
  showHighlightPicker.value = false;
  highlightPickerIndex.value = null;
};

const selectHighlightColor = (colorId) => {
  if (highlightPickerIndex.value === null) return;
  cashItems.value[highlightPickerIndex.value].highlightColor = colorId;
  saveToServer();
  closeHighlightPicker();
};
const itemInputRefs = ref([]);
const taxInsBalance = ref('0.00'); // Initialize as formatted string
const goal = ref('0.00'); // Initialize as formatted string
const percentage = computed(() => {
  const taxIns =
    parseFloat(taxInsBalance.value.toString().replace(/[^0-9.-]/g, '')) || 0;
  const goalValue =
    parseFloat(goal.value.toString().replace(/[^0-9.-]/g, '')) || 0;
  if (goalValue === 0) return '';
  const percent = Math.floor((taxIns / goalValue) * 100);
  return isNaN(percent) ? '' : `${percent}%`;
});

const setItemInputRef = (index) => (el) => {
  itemInputRefs.value[index] = el;
};

// Save the selected date to localStorage
function saveSelectedDateToLocalStorage() {
  localStorage.setItem('mcashPositionLastDate', selectedDate.value);
}

function cashItemsFromSaved(savedCashItems) {
  return renumberCashSequences(
    (savedCashItems?.items || []).map((item) => ({
      ...item,
      slack: !!item.slack,
      weekly: !!item.weekly,
      highlightColor: normalizeHighlightColor(item.highlightColor),
      date: cashItemDayFromSnapshot(item),
    })),
  );
}

async function fetchAdjacentCashPosition(direction) {
  try {
    const response = await axios.get('/api/next-cash-position', {
      params: { date: selectedDate.value, direction },
    });

    const {
      selected_date: savedDate,
      cash_items: savedCashItems, // Object { items, taxInsBalance, goal }
      notes: savedNotes,
    } = response.data;

    if (savedDate) {
      selectedDate.value = new Date(savedDate).toISOString().split('T')[0];
      saveSelectedDateToLocalStorage();
    }
    cashItems.value = cashItemsFromSaved(savedCashItems);
    cashFlowEdit.value = null;
    selectedRows.value.clear();
    // Format fetched values as strings with commas
    taxInsBalance.value = formatNumber(savedCashItems?.taxInsBalance ?? 0);
    goal.value = formatNumber(savedCashItems?.goal ?? 0);
    notes.value = savedNotes || '';
    await nextTick(updateScrollArrowVisibility);
  } catch (error) {
    console.error('Error fetching adjacent cash position:', error);
  }
}

async function saveToServer() {
  try {
    const updatedCashItems = {
      items: cashItems.value,
      taxInsBalance:
        parseFloat(taxInsBalance.value.toString().replace(/[^0-9.-]/g, '')) ||
        0,
      goal: parseFloat(goal.value.toString().replace(/[^0-9.-]/g, '')) || 0,
    };

    await axios.post('/api/cash-position', {
      title: title.value,
      selectedDate: selectedDate.value,
      cashItems: updatedCashItems,
      notes: notes.value,
    });
  } catch (error) {
    console.error('Error saving data:', error);
  }
}

async function fetchData(date = selectedDate.value) {
  try {
    const response = await axios.get('/api/cash-position', {
      params: { date },
    });
    if (!response.data || Object.keys(response.data).length === 0) {
      cashItems.value = [];
      cashFlowEdit.value = null;
      selectedRows.value.clear();
      rowPercentages.value.clear();
      taxInsBalance.value = '0.00'; // Reset to formatted string
      goal.value = '0.00'; // Reset to formatted string
      notes.value = '';
      return response.data;
    }

    const {
      selected_date: savedDate,
      cash_items: savedCashItems, // Object { items, taxInsBalance, goal }
      notes: savedNotes,
    } = response.data;

    if (savedDate) {
      selectedDate.value = new Date(savedDate).toISOString().split('T')[0];
      saveSelectedDateToLocalStorage();
    }
    cashItems.value = cashItemsFromSaved(savedCashItems);
    cashFlowEdit.value = null;
    selectedRows.value.clear();
    // Format fetched values as strings with commas
    taxInsBalance.value = formatNumber(savedCashItems?.taxInsBalance ?? 0);
    goal.value = formatNumber(savedCashItems?.goal ?? 0);
    notes.value = savedNotes || '';
    await nextTick(updateScrollArrowVisibility);
    return response.data;
  } catch (error) {
    console.error('Error fetching data:', error);
    return null;
  }
}

const prevDate = () => fetchAdjacentCashPosition('previous');
const nextDate = () => fetchAdjacentCashPosition('next');

const isHoveringContainer = ref(false);

const isTypingTarget = (el) => {
  if (!el) return false;
  const tag = el.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    el.isContentEditable
  );
};

const onContainerMouseEnter = () => {
  isHoveringContainer.value = true;
};

const onContainerMouseLeave = () => {
  isHoveringContainer.value = false;
};

const onHoverArrowKeydown = (event) => {
  if (event.key === 'Escape' && showCashFlowChart.value && cashFlowEdit.value) {
    event.preventDefault();
    event.stopPropagation();
    cancelCashFlowRowEdit();
    return;
  }
  if (event.key === 'Escape' && showCashFlowChart.value && isHoveringContainer.value) {
    event.preventDefault();
    event.stopPropagation();
    clearCashFlowSearch();
    return;
  }
  if (!isHoveringContainer.value) return;
  if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
  if (isTypingTarget(event.target) || isTypingTarget(document.activeElement)) {
    return;
  }
  event.preventDefault();
  event.stopPropagation();
  if (event.key === 'ArrowLeft') prevDate();
  else nextDate();
};

function shareURL() {
  if (navigator.clipboard && window.isSecureContext) {
    navigator.clipboard
      .writeText(window.location.href)
      .then(() => console.log('URL copied to clipboard.'))
      .catch((err) => console.error('Failed to copy URL:', err));
  } else {
    console.log('Clipboard feature is not available.');
  }
}

onMounted(async () => {
  // First fetch the user role to determine access level
  await fetchUserRole();
  selectedDate.value = getTodayDateKey();
  const todaySnapshot = await fetchData(selectedDate.value);
  if (cashSnapshotOpenChoice(todaySnapshot) === 'previous') {
    await fetchAdjacentCashPosition('previous');
  }
  await openCashFlowChart();
  connectSocket();
  await nextTick(updateScrollArrowVisibility);

  if (typeof ResizeObserver !== 'undefined') {
    scrollResizeObserver = new ResizeObserver(() => {
      updateScrollArrowVisibility();
    });
    if (scrollableContentRef.value) {
      scrollResizeObserver.observe(scrollableContentRef.value);
    }
  }

  window.addEventListener('resize', updateScrollArrowVisibility);
  window.addEventListener('keydown', onHoverArrowKeydown, true);
});

onBeforeUnmount(() => {
  disconnectSocket();
  scrollResizeObserver?.disconnect();
  scrollResizeObserver = null;
  window.removeEventListener('resize', updateScrollArrowVisibility);
  window.removeEventListener('keydown', onHoverArrowKeydown, true);
});

watch(
  () => cashItems.value.length,
  () => nextTick(updateScrollArrowVisibility),
);

async function checkAndSaveDate() {
  try {
    const response = await axios.get('/api/cash-position', {
      params: { date: selectedDate.value },
    });

    const hasData =
      response.data && response.data.cash_items?.items?.length > 0;

    if (hasData) {
      await fetchData(selectedDate.value);
    } else {
      await saveToServer();
    }
  } catch (error) {
    if (error.response?.status === 404) {
      await saveToServer();
    } else {
      console.error('Error checking date:', error);
    }
  }
}

const deleteDate = async () => {
  try {
    await axios.delete('/api/cash-position', {
      params: { date: selectedDate.value },
    });
    prevDate();
  } catch (error) {
    console.error('Error deleting date:', error);
  }
};
const saveDateAndData = () => {
  // Ensure the date value is trimmed and validated
  if (selectedDate.value && selectedDate.value.trim() !== '') {
    saveSelectedDateToLocalStorage();
    checkAndSaveDate();
  }
};
const editLabel = (index) => {
  nextTick(() => {
    const inputEl = itemInputRefs.value[index];
    if (inputEl) {
      inputEl.select();
    }
  });
};

const saveLabelAndData = (index) => {
  if (!cashItems.value[index].label.trim())
    cashItems.value[index].label = 'Label';
  saveToServer();
};
const formatValue = (index) => {
  let value = cashItems.value[index].value.trim().toUpperCase();
  if (/^-?\d*(\.\d{0,2})?K?$/i.test(value))
    cashItems.value[index].value = value;
};
const onAmountInput = (index, event) => {
  cashItems.value[index].value = event.target.value;
  formatValue(index);
};
const onEntryDayInput = (index, event) => {
  const next = filterCashItemDayInput(event.target.value);
  cashItems.value[index].date = next;
  if (event.target.value !== next) event.target.value = next;
};
const commitEntryDay = (index) => {
  cashItems.value[index].date = commitCashItemDay(cashItems.value[index].date);
  saveToServer();
};
const validateAndSaveValue = (index) => {
  let value = cashItems.value[index].value.toUpperCase().replace(/,/g, '');

  // Strip out any "=" characters first
  value = value.replace(/=/g, '');

  let numericValue = 0;

  // Handle expressions like "-1,234.56 + 555.28 - 12.45"
  if (/[+\-*]/.test(value)) {
    // First process any 'K' values in the expression
    value = value.replace(/(\d+(\.\d+)?)[K]/gi, (match, number) => {
      return parseFloat(number) * 1000;
    });

    try {
      // Safely evaluate the expression

      const result = eval(value);
      numericValue = isNaN(result) ? 0 : result;
    } catch (e) {
      console.error('Error evaluating expression:', e);
      numericValue = 0;
    }
  } else {
    // Handle regular values with possible K suffix
    numericValue = value.endsWith('K')
      ? parseFloat(value.slice(0, -1)) * 1000
      : parseFloat(value);
    if (isNaN(numericValue)) numericValue = 0;
  }
  cashItems.value[index].value = formatNumber(numericValue);

  saveToServer();
};
const updateSequence = (index, newSequence) => {
  cashItems.value[index].sequence = parseFloat(newSequence);
  sortAndRenumber();
  saveToServer();
};
const shiftSelectionAfterInsert = (index, count) => {
  if (!count || selectedRows.value.size === 0) return;
  const newSelectedRows = new Set();
  const newRowPercentages = new Map();
  for (const idx of selectedRows.value) {
    const newIdx = idx > index ? idx + count : idx;
    newSelectedRows.add(newIdx);
    if (rowPercentages.value.has(idx)) {
      newRowPercentages.set(newIdx, rowPercentages.value.get(idx));
    }
  }
  selectedRows.value = newSelectedRows;
  rowPercentages.value = newRowPercentages;
  persistSelections();
};
const addWeeklyCopies = (index) => {
  if (!canEdit.value) return;
  const item = cashItems.value[index];
  const copies = weeklyCashItemCopies(item);
  if (!copies.length) return;
  item.weekly = false;
  shiftSelectionAfterInsert(index, copies.length);
  cashItems.value.splice(index + 1, 0, ...copies);
  sortAndRenumber();
  saveToServer();
};
const sortAndRenumber = () => {
  const oldItemsSnapshot = cashItems.value.slice();
  cashItems.value.sort((a, b) => a.sequence - b.sequence);
  renumberItems();
  fixSelectionAfterReorder(oldItemsSnapshot);
};
const renumberItems = () => {
  renumberCashSequences(cashItems.value);
};
const fixSelectionAfterDelete = (deletedIndex) => {
  const newSelectedRows = new Set();
  const newRowPercentages = new Map();
  for (const idx of selectedRows.value) {
    if (idx === deletedIndex) continue;
    const newIdx = idx > deletedIndex ? idx - 1 : idx;
    newSelectedRows.add(newIdx);
    if (rowPercentages.value.has(idx)) {
      newRowPercentages.set(newIdx, rowPercentages.value.get(idx));
    }
  }
  selectedRows.value = newSelectedRows;
  rowPercentages.value = newRowPercentages;
  persistSelections();
};
const fixSelectionAfterReorder = (oldItemsSnapshot) => {
  if (selectedRows.value.size === 0) return;
  const newSelectedRows = new Set();
  const newRowPercentages = new Map();
  for (const oldIndex of selectedRows.value) {
    const item = oldItemsSnapshot[oldIndex];
    if (!item) continue;
    const newIndex = cashItems.value.indexOf(item);
    if (newIndex !== -1) {
      newSelectedRows.add(newIndex);
      if (rowPercentages.value.has(oldIndex)) {
        newRowPercentages.set(newIndex, rowPercentages.value.get(oldIndex));
      }
    }
  }
  selectedRows.value = newSelectedRows;
  rowPercentages.value = newRowPercentages;
  persistSelections();
};
const getSelectionStorageKey = () =>
  `mcashPositionSelections_${selectedDate.value}`;
const loadPersistedSelections = () => {
  try {
    const raw = localStorage.getItem(getSelectionStorageKey());
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    const rows = Array.isArray(parsed?.selectedRows) ? parsed.selectedRows : [];
    const pcts = Array.isArray(parsed?.percentages) ? parsed.percentages : [];
    if (rows.length === 0) return false;
    selectedRows.value = new Set(rows);
    rowPercentages.value = new Map(pcts);
    return true;
  } catch (e) {
    console.error('Error loading persisted selections:', e);
    return false;
  }
};
const persistSelections = () => {
  try {
    const payload = {
      selectedRows: Array.from(selectedRows.value),
      percentages: Array.from(rowPercentages.value.entries()),
    };
    localStorage.setItem(getSelectionStorageKey(), JSON.stringify(payload));
  } catch (e) {
    console.error('Error persisting selections:', e);
  }
};
const saveSelections = () => {
  persistSelections();
  clearSelectedRows();
};

const copiedSelection = ref(null);
const copySelections = () => {
  if (selectedRows.value.size === 0) return;
  copiedSelection.value = Array.from(selectedRows.value);
};
const pasteSelections = () => {
  const indices = copiedSelection.value;
  if (!Array.isArray(indices) || indices.length === 0) return;
  const max = cashItems.value.length;
  const valid = indices.filter((i) => Number.isInteger(i) && i >= 0 && i < max);
  if (valid.length === 0) return;
  selectedRows.value = new Set(valid);
};
const toggleRowSelection = (index) => {
  if (canEdit.value) return;
  let restoredFromStorage = false;
  // On first selection, restore any persisted selections for this date
  if (selectedRows.value.size === 0) {
    restoredFromStorage = loadPersistedSelections();
  }
  if (selectedRows.value.has(index)) {
    if (restoredFromStorage) return;
    percentageModalItem.value = index;
    percentageInput.value = '';
    showPercentageModal.value = true;
  } else {
    selectedRows.value.add(index);
    selectedRows.value = new Set(selectedRows.value);
  }
};
const submitPercentage = () => {
  const pct = parseFloat(percentageInput.value);
  if (!isNaN(pct) && pct >= 0 && pct <= 100) {
    rowPercentages.value.set(percentageModalItem.value, pct);
    rowPercentages.value = new Map(rowPercentages.value);
  }
  closePercentageModal();
};
const closePercentageModal = () => {
  showPercentageModal.value = false;
  percentageModalItem.value = null;
  percentageInput.value = '';
};
const clearPercentage = () => {
  if (percentageModalItem.value !== null) {
    selectedRows.value.delete(percentageModalItem.value);
    rowPercentages.value.delete(percentageModalItem.value);
    selectedRows.value = new Set(selectedRows.value);
    rowPercentages.value = new Map(rowPercentages.value);
  }
  closePercentageModal();
};

watch(showPercentageModal, async (isOpen) => {
  if (!isOpen) return;
  await nextTick();
  percentageInputRef.value?.focus();
  percentageInputRef.value?.select();
});

const isRowSelected = (index) => selectedRows.value.has(index);
const getRowBackgroundColor = (index) => {
  if (!selectedRows.value.has(index)) return 'transparent';

  const pct = rowPercentages.value.get(index) ?? 100;

  // Interpolate from light purple (0%) to dark purple (100%)
  const light = { r: 232, g: 224, b: 236 }; // #e8e0ec
  const dark = { r: 110, g: 88, b: 117 }; // #6e5875

  const ratio = pct / 100;
  const r = Math.round(light.r + (dark.r - light.r) * ratio);
  const g = Math.round(light.g + (dark.g - light.g) * ratio);
  const b = Math.round(light.b + (dark.b - light.b) * ratio);

  return `rgb(${r}, ${g}, ${b})`;
};
const clearSelectedRows = () => {
  selectedRows.value.clear();
  selectedRows.value = new Set();
  rowPercentages.value.clear();
  rowPercentages.value = new Map();
};
const selectedTotal = computed(() => {
  return Array.from(selectedRows.value).reduce((acc, index) => {
    const item = cashItems.value[index];
    const itemAmount = parseFloat(item.value?.replace(/[^0-9.-]/g, '') || 0);
    const pct = rowPercentages.value.get(index) ?? 100;
    return acc + itemAmount * (pct / 100);
  }, 0);
});
const saveNotesAndData = () => saveToServer();
const formatNumber = (num) => {
  return new Intl.NumberFormat('en-US', {
    signDisplay: 'auto',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};
const total = computed(() =>
  snapshotEndTotal(cashItems.value, projectionMonthLength(selectedDate.value)),
);
const slackTotal = computed(() =>
  snapshotSlackTotal(cashItems.value, projectionMonthLength(selectedDate.value)),
);
const residualTotal = computed(() => slackTotal.value + total.value);
const addItem = async () => {
  if (!canEdit.value) {
    canEdit.value = true;
    await nextTick();
  }
  cashItems.value.push({
    label: 'New Item',
    value: '0',
    date: '',
    weekly: false,
    slack: false,
    sequence: nextCashItemSequence(cashItems.value),
    highlightColor: 'none',
  });
  await nextTick();
  scrollCashItemsToBottom('auto');
  updateScrollArrowVisibility();
  const newItemInputEl = itemInputRefs.value[cashItems.value.length - 1];
  if (newItemInputEl) {
    newItemInputEl.focus();
    newItemInputEl.select();
  }
};
const removeItem = (index) => {
  if (cashItems.value.length > 1) {
    fixSelectionAfterDelete(index);
    cashItems.value.splice(index, 1);
  }
  itemInputRefs.value.splice(index, 1);
  renumberItems();
  saveToServer();
};
const isMobile = computed(() => window.innerWidth <= 768);
const sendClose = () => eventBus.emit('requestCloseCash');
const formatTaxInsBalance = () => {
  const numericValue =
    parseFloat(taxInsBalance.value.toString().replace(/[^0-9.-]/g, '')) || 0;
  taxInsBalance.value = formatNumber(numericValue);
  saveToServer();
};
const formatGoal = () => {
  const numericValue =
    parseFloat(goal.value.toString().replace(/[^0-9.-]/g, '')) || 0;
  goal.value = formatNumber(numericValue);
  saveToServer();
};

const getTodayDateKey = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isSelectedDateToday = () =>
  normalizeDateKey(selectedDate.value) === getTodayDateKey();

const showPreviousDayEditConfirm = ref(false);
const showCashFlowChart = ref(false);
const cashFlowRows = ref([]);
const cashFlowDepositByDay = ref({});
const cashFlowEdit = ref(null);
const cashFlowEditDayInput = ref(null);
let cashFlowEditClosedAt = 0;
const cashFlowSearch = ref('');
const cashFlowSearchOpen = ref(false);
const cashFlowSearchInput = ref(null);
const cashFlowVisibleRows = computed(() =>
  filterCashFlowRows(cashFlowRows.value, cashFlowSearch.value),
);
const cashFlowCycleLabel = ref('');
const cashFlowAsOf = ref('');
const cashFlowPriorByDay = ref(null);
const cashFlowCurrentTotal = ref(null);
const cashFlowActualByDay = ref(null);
const cashFlowLoading = ref(false);
const cashFlowError = ref('');

const descriptionPreview = ref(null);

const closeCashFlowChart = () => {
  showCashFlowChart.value = false;
  cashFlowEdit.value = null;
  descriptionPreview.value = null;
  clearCashFlowSearch();
};

function reloadCashFlowRows() {
  const asOf = cashFlowAsOf.value || selectedDate.value;
  cashFlowRows.value = buildCashFlowRows(
    cashFlowDepositByDay.value || {},
    cashItems.value,
    projectionMonthLength(asOf),
    {
      asOfDate: asOf,
      today: new Date(),
      previousByDay: cashFlowPriorByDay.value,
    },
  );
}

function isCashFlowRowEditing(row) {
  return !!(cashFlowEdit.value && row && cashFlowEdit.value.rowId === row.rowId);
}

function showDescriptionPreview(event, row) {
  if (isCashFlowRowEditing(row)) return;
  const word = cashFlowDescriptionWord(row && row.label);
  if (!word) {
    descriptionPreview.value = null;
    return;
  }
  const popup = event.currentTarget.closest('.cash-flow-popup');
  if (!popup) return;
  const anchor = event.currentTarget.getBoundingClientRect();
  const bounds = popup.getBoundingClientRect();
  const gap = 4;
  const width = Math.min(300, Math.max(160, bounds.width - 12));
  let left = anchor.left - bounds.left;
  if (left + width > bounds.width - 6) {
    left = Math.max(6, bounds.width - 6 - width);
  }
  const spaceBelow = bounds.bottom - anchor.bottom - gap;
  const spaceAbove = anchor.top - bounds.top - gap;
  const above = spaceBelow < 140 && spaceAbove > spaceBelow;
  const room = above ? spaceAbove : spaceBelow;
  descriptionPreview.value = {
    word,
    rows: cashFlowDescriptionPreview(cashFlowRows.value, row.label),
    top: above
      ? anchor.top - bounds.top - gap
      : anchor.bottom - bounds.top + gap,
    left,
    width,
    maxHeight: Math.max(72, Math.min(220, room)),
    above,
  };
}

const descriptionPreviewEl = ref(null);

function hideDescriptionPreview(event) {
  const next = event && event.relatedTarget;
  const tip = descriptionPreviewEl.value;
  if (tip && next && (next === tip || tip.contains(next))) return;
  descriptionPreview.value = null;
}

function focusCashFlowEditDay() {
  const target = cashFlowEditDayInput.value;
  const input = Array.isArray(target) ? target.find(Boolean) : target;
  if (!input || typeof input.focus !== 'function') return;
  input.focus();
  if (typeof input.select === 'function') input.select();
}

function cancelCashFlowRowEdit() {
  if (!cashFlowEdit.value) return;
  cashFlowEdit.value = null;
  cashFlowEditClosedAt = Date.now();
}

function beginCashFlowRowEdit(row) {
  descriptionPreview.value = null;
  if (Date.now() - cashFlowEditClosedAt < 500) return;
  if (!canEditCashFlowRow(row, userRole.value)) return;
  if (cashFlowEdit.value && cashFlowEdit.value.rowId === row.rowId) return;
  const item = cashItems.value[row.itemIndex];
  if (!item) return;
  cashFlowEdit.value = {
    rowId: row.rowId,
    itemIndex: row.itemIndex,
    day: item.date || '',
    label: item.label || '',
    amount: item.value == null ? '' : String(item.value),
  };
  nextTick(focusCashFlowEditDay);
}

function onCashFlowEditDay(event) {
  if (!cashFlowEdit.value) return;
  const next = filterCashItemDayInput(event.target.value);
  cashFlowEdit.value.day = next;
  if (event.target.value !== next) event.target.value = next;
}

function saveCashFlowRowEdit() {
  const edit = cashFlowEdit.value;
  if (!edit) return;
  const index = edit.itemIndex;
  const item = cashItems.value[index];
  if (!item) {
    cashFlowEdit.value = null;
    cashFlowEditClosedAt = Date.now();
    return;
  }
  const next = commitCashFlowRowEdit(item, edit);
  cashItems.value[index] = next;
  cashFlowEdit.value = null;
  cashFlowEditClosedAt = Date.now();
  saveToServer();
  reloadCashFlowRows();
}

const toggleCashFlowSearch = () => {
  cashFlowSearchOpen.value = !cashFlowSearchOpen.value;
  if (!cashFlowSearchOpen.value) return;
  nextTick(() => {
    cashFlowSearchInput.value?.focus();
  });
};

const clearCashFlowSearch = () => {
  cashFlowSearch.value = '';
  cashFlowSearchOpen.value = false;
};

const refreshCashFlow = async () => {
  await fetchData(selectedDate.value);
  await openCashFlowChart();
};

const printCashFlow = () => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;
  const title = ['Cash flow', cashFlowCycleLabel.value].filter(Boolean).join(' ');
  printWindow.document.write(
    cashFlowPrintDocument(title, cashFlowVisibleRows.value, cashFlowAsOf.value),
  );
  printWindow.onload = () => {
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };
  printWindow.document.close();
};

const openCashFlowChart = async () => {
  showCashFlowChart.value = true;
  cashFlowLoading.value = true;
  cashFlowError.value = '';
  const asOf = selectedDate.value;
  cashFlowAsOf.value = asOf;
  cashFlowPriorByDay.value = null;
  cashFlowCurrentTotal.value = null;
  cashFlowActualByDay.value = null;
  const range = previousCycleRange(asOf);
  const cycle = currentCycleRange(asOf);
  const projectionMonth = Number(cycle.end.split('-')[1]);
  cashFlowCycleLabel.value = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ][projectionMonth - 1];
  try {
    const response = await axios.get('/api/fetchBankDataforChart', {
      params: { start: range.start, end: projectionFetchEnd(asOf) },
    });
    const previousByDay = cumulativeDepositByDay(
      response.data,
      range.start,
      range.end,
    );
    cashFlowPriorByDay.value = previousByDay;
    cashFlowCurrentTotal.value = currentCycleDepositTotal(asOf, response.data);
    cashFlowActualByDay.value = endingBalanceByProjectionDay(asOf, response.data);
    cashFlowDepositByDay.value = forecastDepositByDay(
      asOf,
      previousByDay,
      response.data,
    );
    reloadCashFlowRows();
  } catch (error) {
    console.error('Error loading cash flow deposits:', error);
    cashFlowError.value = 'Could not load deposit totals.';
    cashFlowDepositByDay.value = {};
    reloadCashFlowRows();
  } finally {
    cashFlowLoading.value = false;
  }
};

const toggleEdit = () => {
  if (canEdit.value) {
    canEdit.value = false;
    showPreviousDayEditConfirm.value = false;
    return;
  }
  if (!isSelectedDateToday()) {
    showPreviousDayEditConfirm.value = true;
    return;
  }
  canEdit.value = true;
};

const confirmPreviousDayEdit = () => {
  showPreviousDayEditConfirm.value = false;
  canEdit.value = true;
};

const cancelPreviousDayEdit = () => {
  showPreviousDayEditConfirm.value = false;
};

const preventBubbling = (event) => {
  // Stop event propagation to ensure the date picker works properly
  event.stopPropagation();
  // Ensure the date picker popup isn't blocked by other elements
  event.target.focus();
};

const createPrintFieldDiv = (input, styleSource, printColor) => {
  const savedPadding = styleSource.style.paddingBottom;
  const savedLineHeight = styleSource.style.lineHeight;
  styleSource.style.paddingBottom = '8px';
  styleSource.style.lineHeight = '1.4';

  const computed = window.getComputedStyle(styleSource);
  const rect = styleSource.getBoundingClientRect();
  const width = Math.round(rect.width);
  const height = Math.round(rect.height);

  styleSource.style.paddingBottom = savedPadding;
  styleSource.style.lineHeight = savedLineHeight;

  const div = document.createElement('div');
  div.textContent = input.value ?? '';
  div.className = input.className;

  div.style.boxSizing = 'border-box';
  div.style.display = 'block';
  div.style.flex = `0 0 ${width}px`;
  div.style.flexGrow = '0';
  div.style.flexShrink = '0';
  div.style.width = `${width}px`;
  div.style.minWidth = `${width}px`;
  div.style.maxWidth = `${width}px`;
  div.style.height = `${height}px`;
  div.style.minHeight = `${height}px`;
  div.style.margin = computed.margin;
  div.style.padding = computed.padding;
  div.style.lineHeight = computed.lineHeight;
  div.style.fontSize = computed.fontSize;
  div.style.fontFamily = computed.fontFamily;
  div.style.fontWeight = computed.fontWeight;
  div.style.textAlign = computed.textAlign;
  div.style.border = computed.border;
  div.style.borderRadius = computed.borderRadius;
  div.style.color = computed.color;
  div.style.whiteSpace = 'nowrap';
  div.style.overflow = 'hidden';
  div.style.alignSelf = computed.alignSelf;
  div.style.setProperty(
    'background-color',
    printColor || computed.backgroundColor || '#ffffff',
    'important',
  );

  return div;
};

const preparePrintCashItemFields = (source, clone, items) => {
  const sourceRows = source.querySelectorAll('.cash-item');
  const cloneRows = clone.querySelectorAll('.cash-item');

  cloneRows.forEach((row, index) => {
    const printColor = HIGHLIGHT_PRINT_COLORS[items[index]?.highlightColor];
    const sourceInputs =
      sourceRows[index]?.querySelectorAll('input[type="text"]') || [];

    row.querySelectorAll('input[type="text"]').forEach((input, inputIndex) => {
      const div = createPrintFieldDiv(
        input,
        sourceInputs[inputIndex] || input,
        printColor,
      );
      input.replaceWith(div);
    });
  });
};

const printPage = async () => {
  try {
    closeHighlightPicker();

    const element = document.querySelector('.cash-position-wrapper');
    if (!element) {
      console.error('cash-position-wrapper not found');
      return;
    }

    // Clone the element to avoid modifying the actual DOM
    const clone = element.cloneNode(true);
    clone.style.position = 'absolute';
    clone.style.left = '-9999px';
    clone.style.top = '0';
    clone.style.width = `${element.offsetWidth}px`;
    clone.style.height = 'auto';
    clone.style.maxHeight = 'none';
    clone.style.overflow = 'visible';
    document.body.appendChild(clone);

    clone
      .querySelectorAll(
        '.highlight-picker-overlay, .selected-total-popup-container',
      )
      .forEach((el) => el.remove());

    // Expand scrollable containers so all rows are captured
    const scrollables = clone.querySelectorAll('.scrollable-content');
    scrollables.forEach((el) => {
      el.style.overflow = 'visible';
      el.style.height = 'auto';
      el.style.maxHeight = 'none';
    });

    // Also expand the inner container
    const container = clone.querySelector('.cash-position-container');
    if (container) {
      container.style.height = 'auto';
      container.style.maxHeight = 'none';
      container.style.overflow = 'visible';
    }

    // Add extra padding to inputs to prevent descender cutoff
    const inputs = clone.querySelectorAll('input');
    inputs.forEach((input) => {
      input.style.paddingBottom = '8px';
      input.style.lineHeight = '1.4';
    });

    preparePrintCashItemFields(
      element,
      clone,
      displayedCashItems.value.map((entry) => entry.item),
    );

    // Capture the cloned element as a canvas
    const canvas = await html2canvas(clone, {
      scale: 2, // Higher quality
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
    });

    // Remove the clone
    document.body.removeChild(clone);

    // Convert canvas to image
    const imageData = canvas.toDataURL('image/jpeg', 0.95);

    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Cash Position</title>
        <style>
          @page {
            size: portrait;
            margin: 0.5in;
          }
          body {
            margin: 0;
            padding: 0;
            display: flex;
            justify-content: center;
            align-items: flex-start;
            height: 100vh;
          }
          img {
            max-width: 100%;
            max-height: 100%;
            width: auto;
            height: auto;
            object-fit: contain;
          }
        </style>
      </head>
      <body>
        <img src="${imageData}" />
      </body>
      </html>
    `);
    printWindow.document.close();

    // Wait for image to load before printing
    printWindow.onload = function () {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };
  } catch (error) {
    console.error('Error printing:', error);
    alert('Error creating print image');
  }
};
</script>

<template>
  <div
    class="cash-position-container"
    @mouseenter="onContainerMouseEnter"
    @mouseleave="onContainerMouseLeave"
  >
    <!-- MOTD Modal Component -->
    <MotdModal :route="$route.path" />

    <div class="title-bar">
      <div class="title-left">
        <span class="title-text">{{ title }}</span>
        <div class="date-container">
          <input
            type="date"
            class="title-date-picker"
            v-model="selectedDate"
            @change="saveDateAndData"
            @click="preventBubbling"
          />
        </div>
      </div>
      <div class="title-buttons">
        <button
          type="button"
          class="chart-button"
          title="Cash flow"
          @click="openCashFlowChart"
        >
          <i class="fas fa-chart-line"></i>
        </button>
        <button class="print-button" @click="printPage">
          <i class="fas fa-print"></i>
        </button>
        <button class="share-button" @click="shareURL">
          <i class="fas fa-share-nodes"></i>
        </button>
        <button class="refresh-button" @click="fetchData()">
          <i class="fas fa-refresh"></i>
        </button>
        <button class="back-button" @click="$router.go(-1)">
          <i class="fas fa-arrow-left"></i>
        </button>
        <button v-if="!isMobile" @click="sendClose" class="home" title="Close">
          <i class="fas fa-close"></i>
        </button>
        <button class="home" title="Home" @click="$router.push('/mhome')">
          <i class="fas fa-home"></i>
        </button>
      </div>
    </div>

    <div v-if="userRole !== 3" class="tax-insurance">
      <span>Tax/Ins. Bal:</span>
      <input
        v-model="taxInsBalance"
        @blur="formatTaxInsBalance"
        :disabled="!canEdit"
        :class="{ 'not-editable': !canEdit }"
      />
      <span style="margin-left: 4px"> Rem:</span>
      <input
        v-model="goal"
        @blur="formatGoal"
        :disabled="!canEdit"
        :class="{ 'not-editable': !canEdit }"
      />
      <span style="margin-left: 4px">{{ ' ' + percentage }}</span>
      <button
        v-if="userRole === 1"
        class="edit-button"
        @click="toggleEdit"
        :class="{ 'edit-active': canEdit }"
      >
        edit
      </button>
    </div>
    <div
      v-if="userRole !== 3 && cashItems.length > 0"
      class="cash-sort-row"
    >
      <button
        type="button"
        class="cash-sort-btn entry-sequence"
        :class="{ active: cashSortKey === 'sequence' }"
        :title="cashSortTitle('sequence')"
        :aria-label="cashSortTitle('sequence')"
        @click="toggleCashSort('sequence')"
      >
        <i class="fas" :class="cashSortIcon('sequence')"></i>
      </button>
      <span class="cash-sort-gap"></span>
      <button
        type="button"
        class="cash-sort-btn entry-day"
        :class="{ active: cashSortKey === 'day' }"
        :title="cashSortTitle('day')"
        :aria-label="cashSortTitle('day')"
        @click="toggleCashSort('day')"
      >
        <i class="fas" :class="cashSortIcon('day')"></i>
      </button>
      <span class="cash-sort-amount"></span>
      <span class="cash-sort-check"></span>
      <span class="cash-sort-check"></span>
      <span class="cash-sort-action"></span>
      <span class="cash-sort-action"></span>
    </div>
    <div
      v-if="userRole !== 3"
      ref="scrollableContentRef"
      class="scrollable-content"
      @scroll="updateScrollArrowVisibility"
    >
      <div v-if="cashItems.length > 0">
        <div class="cash-items">
          <div
            v-for="({ item, index }) in displayedCashItems"
            :key="index"
            class="cash-item"
          >
            <input
              type="text"
              v-model.number="item.sequence"
              @click="!canEdit && toggleRowSelection(index)"
              @blur="updateSequence(index, item.sequence)"
              style="max-width: 20px; text-align: center"
              :readonly="!canEdit"
              :style="
                isRowSelected(index)
                  ? { backgroundColor: getRowBackgroundColor(index) }
                  : {}
              "
              :class="{
                'not-editable': !canEdit && !isRowSelected(index),
                'selected-sequence': isRowSelected(index),
              }"
            />
            <input
              type="text"
              v-model="item.label"
              :ref="setItemInputRef(index)"
              @dblclick="editLabel(index)"
              @blur="saveLabelAndData(index)"
              placeholder="Label"
              class="item-label"
              style="max-width: calc(80% - 36px)"
              :disabled="!canEdit"
              :class="{ 'not-editable': !canEdit }"
            />
            <input
              type="text"
              class="entry-day"
              inputmode="numeric"
              maxlength="2"
              :value="item.date"
              @input="onEntryDayInput(index, $event)"
              @blur="commitEntryDay(index)"
              title="Day of month"
              :disabled="!canEdit"
              :class="{ 'not-editable': !canEdit }"
            />
            <input
              type="text"
              :value="cashAmountFieldText(item.value, canEdit)"
              @input="onAmountInput(index, $event)"
              @blur="validateAndSaveValue(index)"
              placeholder="Value"
              class="right-align"
              style="max-width: 64px"
              :disabled="!canEdit"
              :class="{ 'not-editable': !canEdit }"
            />
            <button
              type="button"
              class="weekly-copy-btn"
              title="Add the next three weekly rows"
              aria-label="Add weekly rows"
              :disabled="!canEdit"
              @click="addWeeklyCopies(index)"
            >
              w
            </button>
            <input
              type="checkbox"
              class="slack-checkbox"
              v-model="item.slack"
              @change="saveToServer"
              :disabled="!canEdit"
              :class="{ 'not-editable': !canEdit }"
              title="Mark as potential slack (may not occur this month)"
            />
            <button
              class="row-action-btn"
              @click="removeItem(index)"
              tabindex="-1"
              :disabled="!canEdit"
            >
              <i class="fas fa-trash"></i>
            </button>
            <button
              class="highlight-color-btn row-action-btn"
              @click="openHighlightPicker(index)"
              tabindex="-1"
              :disabled="!canEdit"
              :title="`Print highlight: ${item.highlightColor || 'none'}`"
              aria-label="Choose print highlight color"
            >
              <span
                class="highlight-color-swatch"
                :class="{
                  'highlight-color-swatch-none': !getHighlightPreviewColor(
                    item.highlightColor,
                  ),
                }"
                :style="
                  getHighlightPreviewColor(item.highlightColor)
                    ? {
                        backgroundColor: getHighlightPreviewColor(
                          item.highlightColor,
                        ),
                      }
                    : {}
                "
              ></span>
            </button>
          </div>
        </div>
      </div>
      <div v-if="cashItems.length == 0">Nothing to show here.</div>
    </div>
    <button
      v-if="userRole !== 3 && showScrollArrow"
      type="button"
      class="scroll-hint"
      aria-label="Scroll to last rows"
      title="Scroll to last rows"
      @click="scrollCashItemsToBottom"
    >
      <i class="fas fa-caret-down"></i>
    </button>
    <div v-if="userRole !== 3" class="bottom-container">
      <div class="actions-total-row">
        <div class="action-buttons-group">
          <button v-if="userRole === 1" class="action-button" @click="addItem">
            <i class="fas fa-plus"></i>
          </button>
          <button class="action-button" @click="prevDate">
            <i class="fas fa-chevron-left"></i>
          </button>
          <button class="action-button" @click="nextDate">
            <i class="fas fa-chevron-right"></i>
          </button>
          <button
            v-if="userRole === 1"
            class="action-button"
            @click="deleteDate"
          >
            <i class="fas fa-trash"></i>
          </button>
        </div>
        <span class="total-value">
          <span class="slack-stack">
            <span class="slack-total slack-line">
              <span>slack:</span>
              <span class="slack-amount">{{ formatNumber(slackTotal) }}</span>
            </span>
            <span v-if="total < 0" class="slack-total slack-line">
              <span>resid.:</span>
              <span class="slack-amount">{{
                formatNumber(residualTotal)
              }}</span>
            </span>
          </span>
          <span class="end-total">End: {{ formatNumber(total) }}</span>
        </span>
      </div>
      <textarea
        v-model="notes"
        @blur="saveNotesAndData"
        placeholder="Add notes here..."
        class="notes-textarea"
        :disabled="!canEdit"
        :class="{ 'not-editable': !canEdit }"
      ></textarea>
    </div>

    <div
      v-if="selectedRows.size > 0"
      class="selected-total-popup-container"
      aria-live="polite"
    >
      <div class="selected-total-popup">
        <button
          class="selected-total-close"
          @click="clearSelectedRows"
          aria-label="Close selected total"
        >
          x
        </button>
        <div class="selected-total-label">Selected Total</div>
        <div class="selected-total-amount">
          {{ formatNumber(selectedTotal) }}
        </div>
        <button
          class="selected-total-save"
          @click="saveSelections"
          aria-label="Save selections"
          title="Save selections for this date"
        >
          <i class="fas fa-save"></i>
        </button>
        <button
          class="selected-total-save selected-total-copy"
          @click="copySelections"
          aria-label="Copy selections"
          title="Copy selected indices"
        >
          <i class="fas fa-copy"></i>
        </button>
        <button
          class="selected-total-save selected-total-paste"
          @click="pasteSelections"
          aria-label="Paste selections"
          title="Paste copied selections on this date"
        >
          <i class="fas fa-paste"></i>
        </button>
      </div>
    </div>

    <div
      v-if="showHighlightPicker"
      class="highlight-picker-overlay"
      @click.self="closeHighlightPicker"
    >
      <div
        class="highlight-picker"
        role="dialog"
        aria-label="Print highlight color"
      >
        <div class="highlight-picker-title">Print highlight</div>
        <div class="highlight-picker-options">
          <button
            v-for="option in HIGHLIGHT_OPTIONS"
            :key="option.id"
            class="highlight-picker-option"
            :class="{
              'highlight-picker-option-active':
                (cashItems[highlightPickerIndex]?.highlightColor || 'none') ===
                option.id,
              'highlight-picker-option-none': option.id === 'none',
            }"
            :title="option.label"
            :aria-label="option.label"
            @click="selectHighlightColor(option.id)"
          >
            <span
              v-if="option.id !== 'none'"
              class="highlight-picker-swatch"
              :style="{ backgroundColor: option.print }"
            ></span>
            <span v-else class="highlight-picker-none-label">none</span>
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="showPreviousDayEditConfirm"
      class="previous-day-confirm-overlay"
      @click.self="cancelPreviousDayEdit"
    >
      <div
        class="percentage-modal previous-day-confirm"
        role="dialog"
        aria-label="Confirm previous day edit"
      >
        <div class="percentage-modal-content">
          <div class="percentage-modal-title">
            are you sure you want to edit a previous day?
          </div>
          <div class="percentage-modal-buttons">
            <button
              class="percentage-btn-cancel"
              @click="cancelPreviousDayEdit"
            >
              Cancel
            </button>
            <button
              class="percentage-btn-submit"
              @click="confirmPreviousDayEdit"
            >
              Edit
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showPercentageModal" class="percentage-modal-overlay">
      <div class="percentage-modal">
        <div class="percentage-modal-content">
          <div class="percentage-modal-title">
            What % of
            {{
              formatNumber(
                parseFloat(
                  cashItems[percentageModalItem]?.value?.replace(
                    /[^0-9.-]/g,
                    '',
                  ) || 0,
                ),
              )
            }}
            would you like to use?
          </div>
          <div class="percentage-input-group">
            <input
              ref="percentageInputRef"
              v-model="percentageInput"
              type="number"
              min="0"
              max="100"
              placeholder="Enter percentage (0-100)"
              class="percentage-input"
              @keyup.enter="submitPercentage"
            />
            <span class="percentage-sign">%</span>
          </div>
          <div class="percentage-modal-buttons">
            <button class="percentage-btn-cancel" @click="closePercentageModal">
              Cancel
            </button>
            <button
              class="percentage-btn-clear"
              @click="clearPercentage"
              title="Remove this row from selection"
            >
              Clear
            </button>
            <button class="percentage-btn-submit" @click="submitPercentage">
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="showCashFlowChart"
      class="cash-flow-overlay"
      @click.self="closeCashFlowChart"
    >
      <div class="cash-flow-popup" role="dialog" aria-label="Cash flow chart">
        <div class="cash-flow-header">
          <span>Cash flow</span>
          <span class="cash-flow-cycle">{{ cashFlowCycleLabel }} as of {{ cashFlowAsOfLabel(cashFlowAsOf) }}</span>
          <button
            type="button"
            class="cash-flow-search"
            title="Filter"
            aria-label="Filter cash flow"
            :aria-pressed="cashFlowSearchOpen ? 'true' : 'false'"
            @click="toggleCashFlowSearch"
          >
            <i class="fas fa-search"></i>
          </button>
          <button
            type="button"
            class="cash-flow-refresh"
            title="Refresh"
            aria-label="Refresh"
            @click="refreshCashFlow"
          >
            <i class="fas fa-sync-alt" :class="{ 'fa-spin': cashFlowLoading }"></i>
          </button>
          <button
            type="button"
            class="cash-flow-print"
            title="Print"
            aria-label="Print"
            @click="printCashFlow"
          >
            <i class="fas fa-print"></i>
          </button>
          <button
            type="button"
            class="cash-flow-close"
            title="Close"
            aria-label="Close cash flow chart"
            @click="closeCashFlowChart"
          >
            <i class="fas fa-close"></i>
          </button>
        </div>
        <div v-if="cashFlowSearchOpen" class="cash-flow-filter">
          <input
            ref="cashFlowSearchInput"
            v-model="cashFlowSearch"
            class="cash-flow-filter-input"
            type="text"
            placeholder="Filter"
            aria-label="Filter cash flow rows"
            autocomplete="off"
          />
          <button
            type="button"
            class="cash-flow-filter-clear"
            title="Clear"
            aria-label="Clear filter"
            @click="cashFlowSearch = ''"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
        <p v-if="cashFlowLoading" class="cash-flow-status">Loading deposits…</p>
        <p v-if="cashFlowError" class="cash-flow-status">{{ cashFlowError }}</p>
        <div class="cash-flow-scroll" @scroll="hideDescriptionPreview">
          <table class="cash-flow-table">
            <thead>
              <tr>
                <th class="day-col">Day</th>
                <th class="desc-col">Description</th>
                <th class="money-col">Amount</th>
                <th class="act-col money-col">Act</th>
                <th class="money-col">Projected</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in cashFlowVisibleRows"
                :key="row.rowId"
                :class="{ 'day-current': isFirstCurrentDayRow(cashFlowVisibleRows, index) }"
                @dblclick="beginCashFlowRowEdit(row)"
              >
                <td
                  class="day-col"
                  :class="{
                    'day-friday': isFirstProjectionFriday(cashFlowAsOf, cashFlowVisibleRows, index),
                  }"
                >
                  <input
                    v-if="isCashFlowRowEditing(row)"
                    ref="cashFlowEditDayInput"
                    class="cash-flow-edit-input"
                    type="text"
                    inputmode="numeric"
                    maxlength="2"
                    :value="cashFlowEdit.day"
                    aria-label="Day"
                    @input="onCashFlowEditDay"
                    @keyup.enter="saveCashFlowRowEdit"
                    @keydown.esc.prevent.stop="cancelCashFlowRowEdit"
                    @dblclick.stop
                  />
                  <template v-else>{{ row.day }}</template>
                </td>
                <td
                  class="desc-col"
                  @mouseenter="showDescriptionPreview($event, row)"
                  @mouseleave="hideDescriptionPreview"
                >
                  <input
                    v-if="isCashFlowRowEditing(row)"
                    v-model="cashFlowEdit.label"
                    class="cash-flow-edit-input"
                    type="text"
                    aria-label="Description"
                    @keyup.enter="saveCashFlowRowEdit"
                    @keydown.esc.prevent.stop="cancelCashFlowRowEdit"
                    @dblclick.stop
                  />
                  <template v-else>{{ row.label }}</template>
                </td>
                <td
                  class="num"
                  :class="{
                    'amount-positive': isPositiveCashFlowAmount(row.amount),
                    'amount-large-expense': isLargeCashFlowExpense(row.amount),
                    'amount-slack': row.slack,
                  }"
                >
                  <input
                    v-if="isCashFlowRowEditing(row)"
                    v-model="cashFlowEdit.amount"
                    class="cash-flow-edit-input"
                    type="text"
                    aria-label="Amount"
                    @keyup.enter="saveCashFlowRowEdit"
                    @keydown.esc.prevent.stop="cancelCashFlowRowEdit"
                    @dblclick.stop
                  />
                  <template v-else>{{ formatProjectionAmount(row.amount) }}</template>
                </td>
                <td
                  class="num"
                  :class="{
                    'actual-first-negative': row.actualFirstNegative && !isCashFlowRowEditing(row),
                    'act-editing': isCashFlowRowEditing(row),
                  }"
                >
                  <button
                    v-if="isCashFlowRowEditing(row)"
                    type="button"
                    class="cash-flow-save"
                    title="Save"
                    aria-label="Save"
                    @click.stop="saveCashFlowRowEdit"
                    @keydown.esc.prevent.stop="cancelCashFlowRowEdit"
                  >
                    <i class="fas fa-check"></i>
                  </button>
                  <template v-else>{{ formatProjectionAmount(row.actual) }}</template>
                </td>
                <td
                  class="num"
                  :class="{
                    'projected-negative': row.negative,
                    'projected-lowest': row.lowest,
                  }"
                  :title="projectedBalanceTooltip(cashFlowAsOf, row.day, cashFlowPriorByDay, cashFlowCurrentTotal, cashFlowActualByDay)"
                >
                  {{ formatProjectionMoney(row.projected) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div
          v-if="descriptionPreview"
          ref="descriptionPreviewEl"
          class="cash-flow-word-preview"
          :class="{ 'cash-flow-word-preview-above': descriptionPreview.above }"
          :style="{
            top: `${descriptionPreview.top}px`,
            left: `${descriptionPreview.left}px`,
            width: `${descriptionPreview.width}px`,
            maxHeight: `${descriptionPreview.maxHeight}px`,
          }"
          @mouseleave="hideDescriptionPreview"
        >
          <div class="cash-flow-word-preview-query">
            {{ descriptionPreview.word }}
          </div>
          <table>
            <thead>
              <tr>
                <th>Day</th>
                <th>Description</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="match in descriptionPreview.rows" :key="match.rowId">
                <td>{{ match.day }}</td>
                <td>{{ match.label }}</td>
                <td>{{ formatProjectionAmount(match.amount) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.cash-position-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-width: 500px;
  /* margin: 10px; */
  /* border-radius: 5px; */
  background: #ddd;
  /* box-shadow: 8px 9px 7px 3px rgb(0 0 0 / 22%); */
  /* border: 1px solid #ccc; */
  position: relative;
  font-size: 0.9em;
}

.title-bar {
  background: #6e5875;
  color: #fff;
  font-size: 1.15em;
  padding: 8px 6px 8px 8px;
  padding-top: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  position: sticky;
  top: 0;
  z-index: 10;
  min-width: 0;
}

.title-left {
  display: flex;
  align-items: center;
  min-width: 0;
  flex: 1;
}

.title-buttons {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
}

.title-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.date-container {
  position: relative;
  display: inline-block;
  margin-right: 4px;
  margin-left: 6px;
  cursor: pointer;
  flex-shrink: 0;
}

.title-date-picker {
  width: 96px;
  height: 22px;
  border: 1px solid #e1dede;
  background-color: #907793;
  border-radius: 5px;
  color: white;
  cursor: pointer;
  /* padding: 2px 4px; */
  font-size: 0.65em;
  text-align: center;
  appearance: none;
  -webkit-appearance: none;
}

.title-date-picker::-webkit-calendar-picker-indicator {
  filter: invert(1);
  cursor: pointer;
  opacity: 0.8;
}

.formatted-date {
  display: none; /* Hide the formatted date display */
}

.cash-sort-row {
  display: flex;
  gap: 2px;
  align-items: center;
  padding: 0 10px 2px;
  flex-shrink: 0;
}

.cash-sort-btn {
  border: 1pt solid transparent;
  background: transparent;
  color: #b7b7b7;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.cash-sort-btn.entry-sequence {
  flex: 1;
  box-sizing: content-box;
  width: 20%;
  max-width: 20px;
  padding: 0 4px;
}

.cash-sort-btn.entry-day {
  flex: 0 0 auto;
  box-sizing: content-box;
  width: 2.6ch;
  max-width: 2.6ch;
  min-width: 2.6ch;
  padding: 0 2px;
}

.cash-sort-btn.active {
  color: #6e5875;
}

.cash-sort-btn i {
  font-size: 0.7em;
}

.cash-sort-gap {
  flex: 1 1 auto;
  width: 20%;
  min-width: 0;
  max-width: calc(80% - 36px);
  box-sizing: content-box;
  padding: 0 4px;
  border: 1pt solid transparent;
}

.cash-sort-amount {
  flex: 0 0 auto;
  box-sizing: content-box;
  width: 20%;
  max-width: 64px;
  padding: 0 4px;
  border: 1pt solid transparent;
}

.cash-sort-check {
  flex: 0 0 auto;
  width: 12px;
  margin-left: 1px;
}

.cash-sort-action {
  flex: 0 0 auto;
  width: 16px;
  min-width: 16px;
}

.scrollable-content {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  padding-top: 0;
  height: calc(100% - 50px);
  /* Hide scrollbar while keeping scrolling functionality */
  -ms-overflow-style: none; /* Internet Explorer and Edge */
  scrollbar-width: none; /* Firefox */
}

/* Hide scrollbar for Chrome, Safari and Opera */
.scrollable-content::-webkit-scrollbar {
  display: none;
}

.scroll-hint {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: 4px;
  padding: 0 0 4px;
  border: none;
  background: transparent;
  color: #907793;
  font-size: 1em;
  line-height: 1;
  flex-shrink: 0;
  cursor: pointer;
}

.scroll-hint:hover {
  color: #6e5875;
}

.back-button,
.refresh-button,
.share-button,
.chart-button,
.print-button,
.home {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid #e1dede;
  color: #eee;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin-right: 3px;
  background: #907793;
  flex-shrink: 0;
}

.title-buttons .home:last-child {
  margin-right: 0;
}

.back-button,
.refresh-button,
.share-button,
.chart-button,
.print-button {
  font-weight: normal;
  font-size: 0.65em;
}

.home {
  font-size: 0.55em;
}

/* Fix for FontAwesome icons in buttons */
.title-bar button i {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

/* Remove the old date selector styles */
.date-selector {
  display: none;
}

.cash-items {
  margin-bottom: 3px;
}
.cash-item button {
  color: #aaa;
  background: none;
  border: none;
}
h2 {
  margin: 0;
  padding: 5px;
  border: 1px solid transparent;
}
h2.editable {
  border: 1px dashed #000;
  outline: none;
}
.cash-item {
  display: flex;
  gap: 2px;
  margin-bottom: 0px;
  align-items: center;
}
.cash-item > input.entry-day,
.cash-item > input.right-align,
.cash-item > .weekly-copy-btn,
.cash-item > .slack-checkbox,
.cash-item > .row-action-btn {
  margin-left: 0;
}
.cash-item > .weekly-copy-btn,
.cash-item > .slack-checkbox {
  margin-left: 1px;
  margin-right: 0;
}
.cash-item input,
.cash-items input {
  width: 20%;
  padding: 4px;
  border-radius: 4px;
  border: 1pt solid #ddd;
  font-size: 0.9em;
  flex: 1;
}

.cash-item input.item-label {
  flex: 1 1 auto;
  min-width: 0;
}
.cash-item input.entry-day {
  flex: 0 0 auto;
  width: 2.6ch;
  max-width: 2.6ch;
  min-width: 2.6ch;
  padding-left: 2px;
  padding-right: 2px;
  text-align: center;
}
.cash-item input.right-align {
  text-align: right;
  flex: 0 0 auto;
}
.cash-items button {
  border-radius: 5px;
  border: none;
  height: 24px;
  background: none;
}
.cash-item .row-action-btn {
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  min-width: 16px;
  padding: 0;
  margin: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.cash-item .row-action-btn i {
  font-size: 0.75em;
}
.highlight-color-btn {
  margin-left: 0 !important;
}
.highlight-color-swatch {
  display: block;
  width: 10px;
  height: 10px;
  border: 1px solid #999;
  border-radius: 1px;
}
.highlight-color-swatch-none {
  background: #fff;
}
.cash-items i {
  color: #b7b7b7;
}

/* New styles for the restructured layout */
.bottom-container {
  display: flex;
  flex-direction: column;
}

.actions-total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.action-buttons-group {
  display: flex;
  gap: 3px;
  margin-left: 10px;
}

.action-button {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid #e1dede;
  background: #907793;
  color: #eee;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.action-button i {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

.total-value {
  display: flex;
  align-items: flex-start;
  font-size: 1.2em;
  font-weight: bold;
  color: #a080a8;
  margin-right: 30px;
}
.end-total {
  margin-top: -3px;
}
.slack-stack {
  display: grid;
  grid-template-columns: auto auto;
  column-gap: 6px;
  row-gap: 0;
  margin-right: 12px;
}
.slack-total {
  font-size: 0.75em;
  font-weight: normal;
  color: #777;
}
.slack-line {
  display: contents;
}
.slack-amount {
  text-align: right;
  font-variant-numeric: tabular-nums;
  justify-self: end;
}
.cash-item > button.weekly-copy-btn {
  flex: 0 0 auto;
  width: 12px;
  height: 14px;
  min-width: 12px;
  padding: 0;
  border: 1px solid #bbb;
  border-radius: 2px;
  background: #fff;
  color: #555;
  font-size: 9px;
  line-height: 12px;
  font-weight: 700;
  cursor: pointer;
}
.cash-item > button.weekly-copy-btn:disabled {
  opacity: 0.45;
  cursor: default;
}
.slack-checkbox {
  flex: 0 0 auto !important;
  width: 12px !important;
  height: 12px;
  margin: 0 0 0 2px;
  align-self: center;
  cursor: pointer;
  accent-color: #b7b7b7;
}

.highlight-picker-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgb(0 0 0 / 25%);
  z-index: 40;
}

.highlight-picker {
  background: rgb(245 245 245 / 98%);
  border-radius: 10px;
  padding: 12px 14px;
  box-shadow: 0 8px 24px rgb(0 0 0 / 25%);
}

.highlight-picker-title {
  font-size: 0.85em;
  color: #444;
  text-align: center;
  margin-bottom: 10px;
  font-weight: 600;
}

.highlight-picker-options {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
}

.highlight-picker-option {
  width: 28px;
  height: 28px;
  border: 2px solid #ccc;
  border-radius: 4px;
  background: #fff;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.highlight-picker-option-active {
  border-color: #6e5875;
  box-shadow: 0 0 0 1px #6e5875;
}

.highlight-picker-swatch {
  width: 18px;
  height: 18px;
  border-radius: 2px;
  border: 1px solid rgb(0 0 0 / 15%);
}

.highlight-picker-none-label {
  font-size: 0.55em;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.highlight-picker-option-none {
  width: 36px;
}

/* Notes textarea */
.notes-textarea {
  width: 94%;
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ddd;
  resize: vertical;
  height: 40px;
  margin-bottom: 0;
}

.tax-insurance {
  font-size: 0.9em;
  color: #907793;
  margin-bottom: 0px;
  display: flex;
  align-items: center;
  margin-top: 5px;
}

.tax-insurance input {
  width: 68px;
  padding: 5px;
  border-radius: 5px;
  border: 1px solid #ddd;
  margin-left: 10px;
  text-align: right;
}

.tax-insurance .edit-button {
  margin-left: auto;
  margin-right: 28px;
  padding: 4px 10px;
  border-radius: 4px;
  border: 1px solid #9487a1;
  background: #ddd;
  color: #333;
  cursor: pointer;
}

.tax-insurance .edit-button.edit-active {
  background: #9487a1;
  color: white;
}

input.not-editable,
textarea.not-editable {
  color: #666;
  background-color: white !important; /* Force white background even when disabled */
  opacity: 1; /* Prevent faded appearance in some browsers */
}

input.selected-sequence {
  color: #fff !important;
  border: 2px solid #fff !important;
}

.selected-total-popup-container {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 25;
}

.selected-total-popup {
  position: relative;
  min-width: 142px;
  padding: 16px 18px 14px;
  border-radius: 10px;
  background: rgb(110 88 117 / 51%);
  color: #fff;
  text-align: center;
  box-shadow: 0 8px 20px rgb(0 0 0 / 20%);
}

.selected-total-label {
  font-size: 0.9em;
  opacity: 0.9;
}

.selected-total-amount {
  margin-top: 4px;
  font-size: 1.3em;
  font-weight: 700;
}

.selected-total-close {
  position: absolute;
  top: 6px;
  right: 8px;
  width: 18px;
  height: 18px;
  border: none;
  border-radius: 50%;
  background: rgb(255 255 255 / 25%);
  color: #fff;
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  pointer-events: auto;
}

.selected-total-save {
  margin-top: 10px;
  width: 32px;
  height: 32px;
  border: 1px solid rgb(255 255 255 / 40%);
  border-radius: 50%;
  background: rgb(255 255 255 / 15%);
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.selected-total-save:hover {
  background: rgb(255 255 255 / 30%);
}

.selected-total-copy {
  margin-left: 30px;
}

.selected-total-paste {
  margin-left: 6px;
}

input {
  outline: none;
}

.percentage-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgb(0 0 0 / 40%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
}

.previous-day-confirm-overlay {
  position: absolute;
  inset: 0;
  background: rgb(0 0 0 / 40%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
}

.percentage-modal {
  background: rgb(220 220 220 / 95%);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 10px 30px rgb(0 0 0 / 30%);
  max-width: 320px;
  width: 90%;
}

.previous-day-confirm {
  max-width: 240px;
  padding: 16px;
}

.cash-flow-overlay {
  position: absolute;
  inset: 0;
  background: rgb(0 0 0 / 40%);
  z-index: 60;
  display: flex;
  padding: 8px;
}

.cash-flow-popup {
  position: relative;
  background: #f4f4f4;
  border-radius: 8px;
  width: 100%;
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  box-shadow: 0 10px 30px rgb(0 0 0 / 30%);
}

.cash-flow-word-preview {
  position: absolute;
  z-index: 5;
  overflow: auto;
  background: #fff;
  color: #222;
  border: 1px solid #c9b8d0;
  border-radius: 4px;
  box-shadow: 0 6px 18px rgb(0 0 0 / 25%);
}

.cash-flow-word-preview-query {
  padding: 3px 6px;
  background: #6e5875;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
}

.cash-flow-word-preview-above {
  transform: translateY(-100%);
}

.cash-flow-word-preview table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.cash-flow-word-preview th,
.cash-flow-word-preview td {
  border-bottom: 1px solid #e4e4e4;
  padding: 3px 6px;
  text-align: left;
  vertical-align: top;
}

.cash-flow-word-preview th {
  position: sticky;
  top: 0;
  background: #eee;
  font-weight: 600;
}

.cash-flow-word-preview td:nth-child(2) {
  white-space: normal;
  word-break: break-word;
}

.cash-flow-word-preview th:last-child,
.cash-flow-word-preview td:last-child {
  text-align: right;
  white-space: nowrap;
}

.cash-flow-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background: #6e5875;
  color: #fff;
  border-radius: 8px 8px 0 0;
}

.cash-flow-cycle {
  flex: 1;
  font-size: 0.85em;
}

.cash-flow-search,
.cash-flow-refresh,
.cash-flow-print {
  background: transparent;
  border: none;
  color: #fff;
  cursor: pointer;
  font-size: 1em;
  line-height: 1;
  padding: 0;
}

.cash-flow-filter {
  position: relative;
  display: flex;
  align-items: center;
  margin: 8px 10px 0;
}

.cash-flow-filter-input {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #c9b8d0;
  border-radius: 4px;
  padding: 4px 22px 4px 8px;
  font-size: 16px;
  outline: none;
}

.cash-flow-filter-clear {
  position: absolute;
  right: 2px;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  background: transparent;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 0 4px;
}

.cash-flow-close {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid #e1dede;
  background: #907793;
  color: #eee;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  flex-shrink: 0;
  cursor: pointer;
  font-size: 0.55em;
}

.cash-flow-close i {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

.cash-flow-status {
  margin: 8px 10px 0;
  color: #666;
}

.cash-flow-scroll {
  overflow: auto;
  flex: 1;
  min-height: 0;
  padding: 6px;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.cash-flow-scroll::-webkit-scrollbar {
  display: none;
}

.cash-flow-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85em;
  table-layout: fixed;
}

.cash-flow-table th,
.cash-flow-table td {
  border-bottom: 1px solid #ddd;
  padding: 3px 4px;
  text-align: left;
  vertical-align: top;
}

.cash-flow-table th {
  position: sticky;
  top: 0;
  background: #e6e6e6;
}

.cash-flow-table .num {
  text-align: right;
  white-space: nowrap;
  padding-left: 2px;
  padding-right: 2px;
}

.cash-flow-table td.projected-negative {
  background: #fde8e8;
  color: #c0392b;
}

.cash-flow-table td.projected-lowest {
  background: #fff0e0;
  color: #e67e22;
}

.cash-flow-table td.actual-first-negative {
  background: #fff0e0;
  color: #e67e22;
}

.cash-flow-table th.day-col,
.cash-flow-table td.day-col {
  text-align: center;
  padding-left: 1px;
  padding-right: 1px;
  white-space: nowrap;
}

.cash-flow-table th.day-col {
  width: 1.8em;
}

.cash-flow-table th.money-col {
  width: 4.8em;
  text-align: right;
  white-space: nowrap;
  padding-left: 2px;
  padding-right: 2px;
}

.cash-flow-table th.desc-col,
.cash-flow-table td.desc-col {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cash-flow-table td.day-friday {
  background: #d4d4d4;
}

.cash-flow-table tr.day-current td {
  background: #e3f2fd;
  color: #1565c0;
}

.cash-flow-table td.amount-positive {
  background: #e5f6e8;
  color: #1c7c3a;
}

.cash-flow-table td.amount-large-expense {
  background: #fdecea;
  color: #c62828;
}

.cash-flow-table tr.day-current td.amount-large-expense {
  background: #fdecea;
  color: #c62828;
}

.cash-flow-table td.amount-slack,
.cash-flow-table tr.day-current td.amount-slack {
  background: #ececec;
}

.cash-flow-edit-input {
  width: 100%;
  box-sizing: border-box;
  border: none;
  border-bottom: 1px solid #6e5875;
  background: #fff;
  font: inherit;
  color: inherit;
  padding: 0;
  margin: 0;
  min-width: 0;
}

.cash-flow-table td.num .cash-flow-edit-input {
  text-align: right;
}

.cash-flow-table td.day-col .cash-flow-edit-input {
  text-align: center;
}

.cash-flow-table td.act-editing {
  text-align: center;
}

.cash-flow-save {
  border: none;
  background: transparent;
  color: #1f9d45;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  font-size: 1em;
}

.percentage-modal-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.percentage-modal-title {
  font-size: 0.95em;
  color: #333;
  font-weight: 600;
  text-align: center;
}

.percentage-input-group {
  position: relative;
  display: flex;
  align-items: center;
}

.percentage-input {
  width: 100%;
  padding: 10px 35px 10px 10px;
  border: 1px solid #999;
  border-radius: 6px;
  font-size: 0.95em;
  text-align: center;
}

.percentage-input:focus {
  outline: none;
  border-color: #6e5875;
  box-shadow: 0 0 4px rgb(110 88 117 / 30%);
}

.percentage-sign {
  position: absolute;
  right: 10px;
  color: #666;
  font-weight: 600;
}

.percentage-modal-buttons {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.percentage-btn-cancel,
.percentage-btn-submit,
.percentage-btn-clear {
  padding: 8px 20px;
  border-radius: 6px;
  border: 1px solid #999;
  font-size: 0.9em;
  cursor: pointer;
  transition: all 0.2s ease;
}

.percentage-btn-cancel {
  background: #ddd;
  color: #333;
}

.percentage-btn-cancel:hover {
  background: #ccc;
}

.percentage-btn-clear {
  background: #999;
  color: #fff;
  border-color: #666;
}

.percentage-btn-clear:hover {
  background: #777;
}

.percentage-btn-submit {
  background: #6e5875;
  color: #fff;
  border-color: #6e5875;
}

.percentage-btn-submit:hover {
  background: #5a3f62;
}

.access-denied {
  text-align: center;
  color: #a080a8;
  font-size: 1.2em;
  margin-top: 20px;
}

/* Print styles */
@media print {
  @page {
    size: portrait;
    margin: 0.5in;
  }

  .cash-position-container {
    box-shadow: none;
    border: none;
    max-width: 100%;
  }

  .title-buttons,
  .cash-flow-overlay {
    display: none !important;
  }

  .action-buttons-group {
    display: none !important;
  }

  .scrollable-content {
    overflow: visible !important;
    height: auto !important;
  }

  .scroll-hint {
    display: none !important;
  }

  .cash-item button {
    display: none !important;
  }

  input[type='date'],
  input,
  textarea {
    border: none !important;
    background: transparent !important;
  }

  .tax-insurance .edit-button {
    display: none !important;
  }
}
</style>
